<template>
    <AppModal
        ref="Modal"
        :title="$t('labels.rewrite_internal_products')"
        size="lg"
        @on-hide="hide"
    >
        <template v-if="task.state.isRunning">
            <div class="modal-body">
                <TaskProgressBar :done="task.state.done" :total="task.state.total"/>
            </div>
        </template>
        <template v-else>
            <div class="modal-body">
                <p>{{ $t('alerts.rewrite_force_update_info1') }}</p>
                <ul>
                    <li>{{ $t('alerts.rewrite_force_update_info2') }}</li>
                    <li>{{ $t('alerts.rewrite_force_update_info3') }}</li>
                    <li>{{ $t('alerts.rewrite_force_update_info4') }}</li>
                </ul>
                <div class="table-responsive">
                    <table class="table table-bordered">
                        <thead>
                            <tr>
                                <th>{{ $t('labels.field') }}</th>
                                <th>{{ $t('labels.update') }}</th>
                                <th>{{ $t('labels.force') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{{ $t('labels.images') }} ({{ $t('labels.main_image') }})</td>
                                <td><AppCheckbox :model-value="rewriteParams.mainImage.shouldUpdate" @update:model-value="(value) => {
                                    rewriteParams.mainImage.shouldUpdate = value,
                                    rewriteParams.mainImage.overwrite = !value ? false : rewriteParams.mainImage.overwrite
                                }"/></td>
                                <td><AppCheckbox :model-value="rewriteParams.mainImage.overwrite" @update:model-value="(value) => {
                                    rewriteParams.mainImage.overwrite = value,
                                    rewriteParams.mainImage.shouldUpdate = value ? true : rewriteParams.mainImage.shouldUpdate
                                }"/></td>
                            </tr>
                            <tr>
                                <td>{{ $t('labels.name') }}</td>
                                <td><AppCheckbox :model-value="rewriteParams.name.shouldUpdate" @update:model-value="(value) => {
                                    rewriteParams.name.shouldUpdate = value,
                                    rewriteParams.name.overwrite = !value ? false : rewriteParams.name.overwrite
                                }"/></td>
                                <td><AppCheckbox :model-value="rewriteParams.name.overwrite" @update:model-value="(value) => {
                                    rewriteParams.name.overwrite = value,
                                    rewriteParams.name.shouldUpdate = value ? true : rewriteParams.name.shouldUpdate
                                }"/></td>
                            </tr>
                            <tr>
                                <td>{{ $t('labels.sku') }}</td>
                                <td><AppCheckbox :model-value="rewriteParams.sku.shouldUpdate" @update:model-value="(value) => {
                                    rewriteParams.sku.shouldUpdate = value,
                                    rewriteParams.sku.overwrite = !value ? false : rewriteParams.sku.overwrite
                                }"/></td>
                                <td><AppCheckbox :model-value="rewriteParams.sku.overwrite" @update:model-value="(value) => {
                                    rewriteParams.sku.overwrite = value,
                                    rewriteParams.sku.shouldUpdate = value ? true : rewriteParams.sku.shouldUpdate
                                }"/></td>
                            </tr>
                            <tr>
                                <td>{{ $t('labels.manufacturer') }}</td>
                                <td><AppCheckbox :model-value="rewriteParams.manufacturer.shouldUpdate" @update:model-value="(value) => {
                                    rewriteParams.manufacturer.shouldUpdate = value,
                                    rewriteParams.manufacturer.overwrite = !value ? false : rewriteParams.manufacturer.overwrite
                                }"/></td>
                                <td><AppCheckbox :model-value="rewriteParams.manufacturer.overwrite" @update:model-value="(value) => {
                                    rewriteParams.manufacturer.overwrite = value,
                                    rewriteParams.manufacturer.shouldUpdate = value ? true : rewriteParams.manufacturer.shouldUpdate
                                }"/></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="row mt-4">
                    <div class="col-lg-6">
                        <InputGroup>
                            <Radio :model-value="rewriteOption === 'selected'" @update:modelValue="rewriteOption = 'selected'" />
                            <template #append>
                                {{ $t('labels.rewrite_only_selected_products', {total: productsUuids.length}) }}
                            </template>
                        </InputGroup>
                    </div>
                    <div class="col-lg-6">
                        <InputGroup>
                            <Radio :model-value="rewriteOption === 'all'" @update:modelValue="rewriteOption = 'all'" />
                            <template #append>
                                {{ $t('labels.confirm_all_products_rewrite', {total: totalProducts}) }}
                            </template>
                        </InputGroup>
                    </div>
                </div>
            </div>
        </template>
        <div class="modal-footer" v-if="!task.state.isRunning">
            <AppButton @click="rewriteProducts" color="indigo">{{ $t('buttons.rewrite') }}</AppButton>
        </div>
    </AppModal>
</template>
<script>
import AppModal from '../../../../../../components/new/modal/AppModal.vue';
import AppCheckbox from '../../../../../../components/new/form/checkbox/AppCheckbox.vue';
import AppButton from '../../../../../../components/new/button/AppButton.vue';
import TaskProgressBar from '../../../../../../components/new/progress/TaskProgressBar.vue';
import Radio from '../../../../../../components/new/form/radio/Radio.vue';
import InputGroup from '../../../../../../components/new/form/InputGroup.vue';

import { chunk } from '../../../../../../utils';
import { httpClient } from '../../../../../../services/HttpClient';
import HttpRequest from '../../../../../../services/Http/HttpRequest';
import HttpSender from '../../../../../../services/Http/HttpSender';
import MessageNotificator from '../../../../../../services/MessageNotificator';
import { createProgressTask } from '../../../../../../services/Progress/ProgressTask';

const PAGE_LIMIT = 100;

function isEmpty(value) {
    return value === null || value === undefined || value === '';
}

function defaultRewriteParams() {
    return {
        mainImage: { shouldUpdate: false, overwrite: false },
        name: { shouldUpdate: false, overwrite: false },
        sku: { shouldUpdate: false, overwrite: false },
        manufacturer: { shouldUpdate: false, overwrite: false }
    };
}

export default {
    name: 'RewriteInternalProductsModal',
    components: {
        AppModal, AppCheckbox, AppButton, TaskProgressBar, Radio, InputGroup
    },
    props: {
        integration: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            task: createProgressTask(),
            productsUuids: [],
            rewriteParams: defaultRewriteParams(),
            filters: {},
            allChecked: false,
            totalProducts: 0,
            rewriteOption: 'selected'
        }
    },
    methods: {
        show(productsUuids, filters, allChecked, totalProducts) {
            this.filters = filters;
            this.productsUuids = productsUuids;
            this.allChecked = allChecked;
            this.totalProducts = totalProducts;
            this.$refs.Modal.show();
        },
        hide() {
            this.task.cancel();
            this.$refs.Modal.hide();
            this.productsUuids = [];
            this.allChecked = false;
            this.totalProducts = 0;
            this.rewriteOption = 'selected';
            this.rewriteParams = defaultRewriteParams();
            this.filters = {};
        },
        async fetchIntegrationProducts({tick}) {
            const url = `/integrations/${this.$route.params.integrationUuid}/products`;
            const productsRequests = [];
            const products = [];

            if (this.rewriteOption === 'all') {
                let totalPages = 0;

                await httpClient.get(url, {
                    limit: 1,
                    ...this.filters,
                    has_product: 1
                }).then(response => {
                    totalPages = Math.ceil(response.data.meta.total / PAGE_LIMIT);
                });

                for (let page = 1; page <= totalPages; page++) {
                    productsRequests.push(new HttpRequest('GET', url, {
                        limit: PAGE_LIMIT,
                        page: page,
                        has_product: 1,
                        ...this.filters
                    }));
                }
            } else {
                for (let uuids of chunk(this.productsUuids, PAGE_LIMIT)) {
                    productsRequests.push(new HttpRequest('GET', url, {
                        uuids: uuids,
                        limit: PAGE_LIMIT,
                        has_product: 1,
                        ...this.filters
                    }));
                }
            }

            await new HttpSender().sendRequests(productsRequests, (request, response) => {
                tick();

                for (const integrationProduct of response.body.data) {
                    products.push({
                        internalUuid: integrationProduct.product.uuid,
                        name: integrationProduct.name,
                        sku: integrationProduct.sku,
                        manufacturerName: integrationProduct.manufacturer_name,
                        pictureUrl: integrationProduct.picture_url
                    });
                }
            });

            return products;
        },
        async fetchInternalProductsByUuid(internalUuids) {
            const requests = [];
            const internalProductsByUuid = {};

            for (let uuids of chunk(internalUuids, PAGE_LIMIT)) {
                requests.push(new HttpRequest('GET', '/catalog/product-items', {
                    uuids: uuids,
                    limit: PAGE_LIMIT,
                    extra: 0
                }));
            }

            await new HttpSender().sendRequests(requests, (request, response) => {
                for (const internalProduct of response.body.data) {
                    internalProductsByUuid[internalProduct.uuid] = internalProduct;
                }
            });

            return internalProductsByUuid;
        },
        async fetchExistingImageRelationships(internalUuids) {
            const requests = [];
            const relationshipsByProductUuid = {};

            for (let uuids of chunk(internalUuids, PAGE_LIMIT)) {
                requests.push(new HttpRequest('GET', '/file/file-relationships', {
                    model_types: ['product_item'],
                    model_uuids: uuids,
                    limit: 0
                }));
            }

            await new HttpSender().sendRequests(requests, (request, response) => {
                for (const relationship of response.body.data) {
                    if (!relationshipsByProductUuid[relationship.model_uuid]) {
                        relationshipsByProductUuid[relationship.model_uuid] = [];
                    }
                    relationshipsByProductUuid[relationship.model_uuid].push(relationship);
                }
            });

            return relationshipsByProductUuid;
        },
        async createMainImageRelationships(products, internalProductsByUuid, {tick}) {
            const productsToUpdate = products.filter(product => {
                if (!product.pictureUrl || !this.rewriteParams.mainImage.shouldUpdate) {
                    return false;
                }

                const internalProduct = internalProductsByUuid[product.internalUuid] || {};

                return this.rewriteParams.mainImage.overwrite || isEmpty(internalProduct.main_image_url);
            });

            const existingRelationshipsByProductUuid = await this.fetchExistingImageRelationships(
                productsToUpdate.map(product => product.internalUuid)
            );

            const mainImageRelationshipUuidByProductUuid = {};
            const requests = [];

            for (const product of productsToUpdate) {
                const existingRelationship = (existingRelationshipsByProductUuid[product.internalUuid] || [])
                    .find(relationship => relationship.origin_url === product.pictureUrl);

                if (existingRelationship) {
                    mainImageRelationshipUuidByProductUuid[product.internalUuid] = existingRelationship.uuid;
                    continue;
                }

                requests.push(new HttpRequest('POST', '/file/file-relationships', {
                    model_uuid: product.internalUuid,
                    origin_url: product.pictureUrl
                }));
            }

            await new HttpSender().sendRequestsInBatches(requests, (request, response) => {
                tick();

                if (response.body.model_uuid) {
                    mainImageRelationshipUuidByProductUuid[response.body.model_uuid] = response.body.uuid;
                }
            });

            return mainImageRelationshipUuidByProductUuid;
        },
        buildProductUpdateRequestBody(product, internalProductsByUuid, mainImageRelationshipUuidByProductUuid) {
            const internalProduct = internalProductsByUuid[product.internalUuid] || {};
            const requestBody = {};

            if (this.rewriteParams.name.shouldUpdate && !isEmpty(product.name)) {
                if (this.rewriteParams.name.overwrite || isEmpty(internalProduct.name)) {
                    requestBody.name = product.name;
                }
            }

            if (this.rewriteParams.sku.shouldUpdate && !isEmpty(product.sku)) {
                if (this.rewriteParams.sku.overwrite || isEmpty(internalProduct.sku)) {
                    requestBody.sku = product.sku;
                }
            }

            if (this.rewriteParams.manufacturer.shouldUpdate && !isEmpty(product.manufacturerName)) {
                if (this.rewriteParams.manufacturer.overwrite || isEmpty(internalProduct.manufacturer)) {
                    requestBody.manufacturer = product.manufacturerName;
                }
            }

            if (mainImageRelationshipUuidByProductUuid[product.internalUuid]) {
                requestBody.main_image_uuid = mainImageRelationshipUuidByProductUuid[product.internalUuid];
            }

            return requestBody;
        },
        splitProductsByInternalProduct(products) {
            const countByInternalUuid = {};

            for (const product of products) {
                countByInternalUuid[product.internalUuid] = (countByInternalUuid[product.internalUuid] ?? 0) + 1;
            }

            return {
                products: products.filter(product => countByInternalUuid[product.internalUuid] === 1),
                skippedCount: Object.values(countByInternalUuid).filter(count => count > 1).length
            };
        },
        async rewriteProducts() {
            let skippedCount = 0;

            const completed = await this.task.run(async (progress) => {
                const split = this.splitProductsByInternalProduct(await this.fetchIntegrationProducts(progress));
                const products = split.products;
                skippedCount = split.skippedCount;
                const internalProductsByUuid = await this.fetchInternalProductsByUuid(products.map(product => product.internalUuid));
                const mainImageRelationshipUuidByProductUuid = await this.createMainImageRelationships(
                    products,
                    internalProductsByUuid,
                    progress,
                );

                const requests = [];

                for (const product of products) {
                    const requestBody = this.buildProductUpdateRequestBody(product, internalProductsByUuid, mainImageRelationshipUuidByProductUuid);

                    if (Object.keys(requestBody).length > 0) {
                        requests.push(new HttpRequest('PATCH', `/catalog/product-items/${product.internalUuid}`, requestBody));
                    }
                }

                await new HttpSender().sendRequestsInBatches(requests, () => progress.tick());
            });

            if (completed) {
                this.hide();

                if (skippedCount > 0) {
                    new MessageNotificator().warning(this.$t('alerts.rewrite_skipped_multiple_publications', {total: skippedCount}));
                }
            }
        }
    },
    beforeUnmount() {
        this.task.cancel();
    }
}
</script>
